import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Button, Flex, Heading, Text, Tabs, TabList, Tab, TabPanels, TabPanel,
    FormControl, FormLabel, Input, useToast, VStack, Spinner, InputGroup, InputRightAddon, InputLeftAddon, Icon,
    SimpleGrid, Divider, Table, Thead, Tbody, Tr, Th, Td, IconButton, HStack, Badge, List, ListItem,
    AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader, AlertDialogContent, AlertDialogOverlay,
    Progress, useBreakpointValue,
} from '@chakra-ui/react';
import { FaSave, FaCog, FaCalculator, FaTruck, FaTags, FaTrash, FaPlus, FaShippingFast, FaBan, FaBoxOpen, FaStore } from 'react-icons/fa';
import {
    getConfiguracoes, updateConfiguracoes, getPrecoCalculado, getPrecoCalculadoEcommerce,
    getMargensFornecedores, updateMargensFornecedores,
    getMargensMarcas, saveMargemMarca, deleteMargemMarca, getSugestoesMarcas,
    getMargensContasMl, updateMargensContasMl,
    getExpedicaoFornecedores, updateExpedicaoFornecedores,
    getExpedicaoAtualizacoes, cancelarExpedicaoAtualizacao,
} from '../services/api';
import Preloader from '../components/Preloader';

// Vivem na aba Frete e Envio, nao na de precificacao, que lista o resto da tabela.
const CONFIGS_FRETE = ['frete_alerta_percentual', 'frete_alerta_valor'];

const Configuracoes = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [configs, setConfigs] = useState([]);
    const [initialConfigs, setInitialConfigs] = useState([]);
    const toast = useToast();

    useEffect(() => {
        const fetchConfigs = async () => {
            setLoading(true);
            try {
                const data = await getConfiguracoes();
                if (data) {
                    const formattedData = data.map(c => ({
                        ...c,
                        valor: String(c.valor).replace('.', ',')
                    }));
                    setConfigs(formattedData);
                    setInitialConfigs(JSON.parse(JSON.stringify(formattedData)));
                } else {
                    toast({
                        title: 'Erro ao carregar configurações.',
                        status: 'error',
                        duration: 5000,
                        isClosable: true,
                    });
                }
            } finally {
                setLoading(false);
            }
        };
        fetchConfigs();
    }, [toast]);

    const handleConfigChange = (nome, rawValue) => {
        let value = rawValue.replace(/[^0-9,]/g, '');
        const parts = value.split(',');
        if (parts.length > 2) {
            value = parts[0] + ',' + parts.slice(1).join('');
        }
        setConfigs(prev =>
            prev.map(c => (c.nome === nome ? { ...c, valor: value } : c))
        );
    };

    const handleSave = async () => {
        setSaving(true);
        const configsParaSalvar = configs.map(config => ({
            ...config,
            valor: parseFloat(String(config.valor).replace(',', '.')) || 0,
        }));

        try {
            await updateConfiguracoes(configsParaSalvar);
            toast({
                title: 'Sucesso!',
                description: 'Configurações salvas com sucesso.',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            setInitialConfigs(JSON.parse(JSON.stringify(configs)));
        } catch (error) {
            toast({
                title: 'Erro ao salvar.',
                description: 'Não foi possível atualizar as configurações.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setSaving(false);
        }
    };

    const hasChanges = JSON.stringify(configs) !== JSON.stringify(initialConfigs);

    return (
        <>
            {loading && <Preloader />}
            <Box textAlign="center" fontSize="xl" py={4} bg="white" minH="100vh">
                <Box textAlign="center" ml={4} mb={4} fontSize="xl">
                    <Text color={"#F68633"} fontWeight={"bold"} my={4}>
                        CONFIGURAÇÕES GERAIS
                    </Text>
                </Box>
                <Box mx={{ base: 2, md: 12, lg: 20 }} mt={0} boxShadow='md' borderRadius="lg" px={0} mb={"10"} pb={2}>
                    <Tabs variant="enclosed-colored" isLazy>
                        <TabList
                            mb="1em"
                            overflowX={{ base: "auto", md: "visible" }}
                            overflowY="hidden"
                            sx={{ scrollbarWidth: "thin" }}
                            whiteSpace="nowrap"
                        >
                            <Tab
                                bg="gray.100"
                                color="gray.800"
                                fontSize={{ base: "sm", md: "md" }}
                                px={{ base: 3, md: 4 }}
                                flexShrink={0}
                                _selected={{ color: 'white', bg: 'gray.700', fontWeight: 'bold' }}
                            >
                                <Icon as={FaCog} mr={2} />
                                <Text as="span" display={{ base: "none", sm: "inline" }}>Configurações de Precificação</Text>
                                <Text as="span" display={{ base: "inline", sm: "none" }}>Precificação</Text>
                            </Tab>
                            <Tab
                                bg="gray.100"
                                color="gray.800"
                                fontSize={{ base: "sm", md: "md" }}
                                px={{ base: 3, md: 4 }}
                                flexShrink={0}
                                _selected={{ color: 'white', bg: 'gray.700', fontWeight: 'bold' }}
                            >
                                <Icon as={FaTruck} mr={2} />
                                <Text as="span" display={{ base: "none", sm: "inline" }}>Margem por Fornecedor</Text>
                                <Text as="span" display={{ base: "inline", sm: "none" }}>Fornecedor</Text>
                            </Tab>
                            <Tab
                                bg="gray.100"
                                color="gray.800"
                                fontSize={{ base: "sm", md: "md" }}
                                px={{ base: 3, md: 4 }}
                                flexShrink={0}
                                _selected={{ color: 'white', bg: 'gray.700', fontWeight: 'bold' }}
                            >
                                <Icon as={FaTags} mr={2} />
                                <Text as="span" display={{ base: "none", sm: "inline" }}>Margem por Marca</Text>
                                <Text as="span" display={{ base: "inline", sm: "none" }}>Marca</Text>
                            </Tab>
                            <Tab
                                bg="gray.100"
                                color="gray.800"
                                fontSize={{ base: "sm", md: "md" }}
                                px={{ base: 3, md: 4 }}
                                flexShrink={0}
                                _selected={{ color: 'white', bg: 'gray.700', fontWeight: 'bold' }}
                            >
                                <Icon as={FaStore} mr={2} />
                                <Text as="span" display={{ base: "none", sm: "inline" }}>Margem por Conta ML</Text>
                                <Text as="span" display={{ base: "inline", sm: "none" }}>Conta ML</Text>
                            </Tab>
                            <Tab
                                bg="gray.100"
                                color="gray.800"
                                fontSize={{ base: "sm", md: "md" }}
                                px={{ base: 3, md: 4 }}
                                flexShrink={0}
                                _selected={{ color: 'white', bg: 'gray.700', fontWeight: 'bold' }}
                            >
                                <Icon as={FaShippingFast} mr={2} />
                                <Text as="span" display={{ base: "none", sm: "inline" }}>Expedição ML</Text>
                                <Text as="span" display={{ base: "inline", sm: "none" }}>Expedição</Text>
                            </Tab>
                            <Tab
                                bg="gray.100"
                                color="gray.800"
                                fontSize={{ base: "sm", md: "md" }}
                                px={{ base: 3, md: 4 }}
                                flexShrink={0}
                                _selected={{ color: 'white', bg: 'gray.700', fontWeight: 'bold' }}
                            >
                                <Icon as={FaBoxOpen} mr={2} />
                                <Text as="span" display={{ base: "none", sm: "inline" }}>Frete e Envio</Text>
                                <Text as="span" display={{ base: "inline", sm: "none" }}>Frete</Text>
                            </Tab>
                        </TabList>
                        <TabPanels>
                            <TabPanel>
                                <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={10} p={{ base: 2, md: 6 }}>
                                    <VStack spacing={6} align="start">
                                        {configs.filter(c => !CONFIGS_FRETE.includes(c.nome)).map(config => {
                                            const isPercent = config.label.includes('%');
                                            const isCurrency = config.label.includes('(R$)');
                                            return (
                                                <FormControl key={config.nome} maxW={{ base: "100%", md: "350px" }}>
                                                    <FormLabel htmlFor={config.nome} mb={1} fontSize="md" textAlign="left">
                                                        {config.label}
                                                    </FormLabel>
                                                    <InputGroup>
                                                        {isCurrency && <InputLeftAddon>R$</InputLeftAddon>}
                                                        <Input
                                                            id={config.nome}
                                                            type="text"
                                                            value={config.valor}
                                                            onChange={(e) => handleConfigChange(config.nome, e.target.value)}
                                                            bg="white"
                                                            placeholder={`Digite o valor para ${config.label}`}
                                                        />
                                                        {isPercent && <InputRightAddon>%</InputRightAddon>}
                                                    </InputGroup>
                                                </FormControl>
                                            );
                                        })}
                                        <Flex justifyContent="center" mt={6} w="100%">
                                            <Button
                                                leftIcon={<FaSave />}
                                                colorScheme="green"
                                                onClick={handleSave}
                                                isLoading={saving}
                                                isDisabled={!hasChanges || saving}
                                            >
                                                Salvar Configurações
                                            </Button>
                                        </Flex>
                                    </VStack>

                                    <VStack spacing={6} align="stretch">
                                        <CalculadoraPreco
                                            titulo="Calculadora de Precificação (Mercado Livre)"
                                            calcularFn={getPrecoCalculado}
                                            comPremium
                                        />
                                        <CalculadoraPreco
                                            titulo="Calculadora de Precificação (E-commerce)"
                                            calcularFn={getPrecoCalculadoEcommerce}
                                        />
                                    </VStack>
                                </SimpleGrid>
                            </TabPanel>
                            <TabPanel>
                                <MargemFornecedoresTab />
                            </TabPanel>
                            <TabPanel>
                                <MargemMarcasTab />
                            </TabPanel>
                            <TabPanel>
                                <MargemContasMlTab />
                            </TabPanel>
                            <TabPanel>
                                <ExpedicaoMLTab />
                            </TabPanel>
                            <TabPanel>
                                <FreteEnvioTab />
                            </TabPanel>
                        </TabPanels>
                    </Tabs>
                </Box>
            </Box>
        </>
    );
};

const MOTIVOS_PREMIUM = {
    limite_79: 'No Premium passaria de R$ 79 (frete grátis): fica Clássico',
    tarifa_indisponivel: 'Tarifa do ML indisponível: fica no preço Clássico',
    frete_indisponivel: 'Frete do ML indisponível: fica no preço Clássico',
    sem_convergencia: 'Tarifa oscila na faixa de preço: fica no preço Clássico',
    sem_categoria: 'Categoria do anúncio desconhecida',
    tarifa_invalida: 'Tarifa inválida',
    preco_invalido: 'Preço inválido',
};

const pctBR = (v) => `${(Number(v) * 100).toFixed(2).replace('.', ',').replace(/,00$/, '')}%`;

const LinhaPremium = ({ calculo, rotulo, formatarPreco }) => (
    <Box bg="purple.50" borderWidth="1px" borderColor="purple.100" p={3} borderRadius="md" textAlign="left">
        <Flex justify="space-between" align="center" gap={2} flexWrap="wrap">
            <Text fontWeight="bold" fontSize="sm">{rotulo}</Text>
            {calculo.convertido ? (
                <Text fontWeight="bold" fontSize="lg" color="purple.700">R$ {formatarPreco(calculo.preco)}</Text>
            ) : (
                <Badge colorScheme="gray">Clássico R$ {formatarPreco(calculo.preco)}</Badge>
            )}
        </Flex>
        {calculo.convertido ? (
            <Text fontSize="xs" color="gray.600" mt={1}>
                Tarifa {pctBR(calculo.pct_classico)} → {pctBR(calculo.pct_premium)} · imposto {pctBR(calculo.imposto)}
                {calculo.frete_classico != null && ` · frete R$ ${formatarPreco(calculo.frete_classico)}${Number(calculo.frete_premium) !== Number(calculo.frete_classico) ? ` → R$ ${formatarPreco(calculo.frete_premium)}` : ''}`}
                {` · fator ×${Number(calculo.fator).toFixed(4).replace('.', ',')}`}
            </Text>
        ) : (
            <Text fontSize="xs" color="gray.600" mt={1}>{MOTIVOS_PREMIUM[calculo.motivo] || calculo.motivo}</Text>
        )}
    </Box>
);

const CalculadoraPreco = ({ titulo, calcularFn, comPremium = false }) => {
    const toast = useToast();
    const [custoTeste, setCustoTeste] = useState('');
    const [anuncioTeste, setAnuncioTeste] = useState('');
    const [resultadoPreco, setResultadoPreco] = useState(null);
    const [isCalculating, setIsCalculating] = useState(false);

    const handleCustoChange = (rawValue) => {
        let value = rawValue.replace(/[^0-9,]/g, '');
        const parts = value.split(',');
        if (parts.length > 2) {
            value = parts[0] + ',' + parts.slice(1).join('');
        }
        setCustoTeste(value);
    };

    const handleCalcularPreco = async () => {
        if (!custoTeste) {
            toast({ title: 'Por favor, insira um valor de custo.', status: 'warning', duration: 3000, isClosable: true });
            return;
        }
        setIsCalculating(true);
        setResultadoPreco(null);
        const custoNumerico = parseFloat(String(custoTeste).replace(',', '.')) || 0;

        try {
            const data = comPremium ? await calcularFn(custoNumerico, anuncioTeste.trim()) : await calcularFn(custoNumerico);
            if (data) {
                setResultadoPreco(data);
            } else {
                toast({ title: 'Erro ao calcular o preço.', status: 'error', duration: 5000, isClosable: true });
            }
        } finally {
            setIsCalculating(false);
        }
    };

    const formatarPreco = (valor) => {
        const numero = Number(valor);
        if (isNaN(numero)) return "0,00";
        return numero.toFixed(2).replace(".", ",");
    };

    return (
        <VStack spacing={4} align="stretch" p={6} bg="gray.50" borderRadius="lg" borderWidth="1px">
            <Heading as="h3" size="md" textAlign="center">{titulo}</Heading>
            <FormControl>
                <FormLabel>Custo do Produto (R$)</FormLabel>
                <InputGroup>
                    <InputLeftAddon>R$</InputLeftAddon>
                    <Input
                        type="text"
                        bg="white"
                        placeholder="100,00"
                        value={custoTeste}
                        onChange={(e) => handleCustoChange(e.target.value)}
                    />
                </InputGroup>
            </FormControl>
            {comPremium && (
                <FormControl>
                    <FormLabel>Anúncio para tarifa real</FormLabel>
                    <Input
                        type="text"
                        bg="white"
                        placeholder="MLB... (opcional)"
                        value={anuncioTeste}
                        onChange={(e) => setAnuncioTeste(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                    />
                </FormControl>
            )}
            <Button
                leftIcon={<FaCalculator />}
                colorScheme="orange"
                onClick={handleCalcularPreco}
                isLoading={isCalculating}
            >
                Calcular Preço de Venda
            </Button>

            {resultadoPreco && (
                <Box pt={4}>
                    <Divider my={4} />
                    <Text fontSize="lg" fontWeight="bold">Resultado:</Text>
                    <Flex justify="space-between" align="center" bg="green.100" p={3} borderRadius="md" mt={2}>
                        <Text fontWeight="bold">{comPremium ? (resultadoPreco.real ? 'Clássico (fórmula padrão):' : 'Preço Clássico:') : 'Preço de Venda:'}</Text>
                        <Text fontWeight="bold" fontSize="xl" color="green.700">
                            R$ {formatarPreco(resultadoPreco.preco)}
                        </Text>
                    </Flex>
                    {comPremium && resultadoPreco.real && (
                        <Box bg="green.50" borderWidth="1px" borderColor="green.100" p={3} borderRadius="md" mt={2} textAlign="left">
                            <Flex justify="space-between" align="center" gap={2} flexWrap="wrap">
                                <Text fontWeight="bold" fontSize="sm">Clássico com tarifa e frete reais ({resultadoPreco.real.anuncio}):</Text>
                                {resultadoPreco.real.erro ? null : (
                                    <Text fontWeight="bold" fontSize="lg" color="green.700">R$ {formatarPreco(resultadoPreco.real.preco)}</Text>
                                )}
                            </Flex>
                            <Text fontSize="xs" color={resultadoPreco.real.erro ? 'red.600' : 'gray.600'} mt={1}>
                                {resultadoPreco.real.erro || `Tarifa ${pctBR(resultadoPreco.real.tarifa)} · frete R$ ${formatarPreco(resultadoPreco.real.frete)}`}
                            </Text>
                        </Box>
                    )}
                    {comPremium && resultadoPreco.premium && (
                        <VStack align="stretch" spacing={2} mt={3}>
                            <Text fontSize="md" fontWeight="bold">Preço Premium (mesmo lucro):</Text>
                            {resultadoPreco.premium.erro && (
                                <Text fontSize="sm" color="red.600">{resultadoPreco.premium.erro}</Text>
                            )}
                            {resultadoPreco.premium.modo === 'anuncio' && !resultadoPreco.premium.erro && (
                                <LinhaPremium
                                    calculo={resultadoPreco.premium}
                                    rotulo={`${resultadoPreco.premium.anuncio} · ${resultadoPreco.premium.categoria}`}
                                    formatarPreco={formatarPreco}
                                />
                            )}
                            {resultadoPreco.premium.modo === 'estimativa' && resultadoPreco.premium.cenarios.map((c) => (
                                <LinhaPremium
                                    key={`${c.pct_classico}-${c.pct_premium}-${c.motivo}`}
                                    calculo={c}
                                    rotulo={c.pct_classico != null ? `Estimativa ${pctBR(c.pct_classico)} → ${pctBR(c.pct_premium)}` : 'Estimativa'}
                                    formatarPreco={formatarPreco}
                                />
                            ))}
                        </VStack>
                    )}
                    <Text fontSize="md" fontWeight="bold" mt={4} mb={2}>Premissas Utilizadas:</Text>
                    <VStack align="stretch" spacing={1} bg="gray.100" p={3} borderRadius="md">
                        {resultadoPreco.premissas.map(premisa => (
                            <Flex key={premisa.label} justify="space-between" fontSize="sm">
                                <Text>{premisa.label}:</Text>
                                <Text fontWeight="bold">
                                    {premisa.label.includes('(R$)') ? `R$ ${formatarPreco(premisa.valor)}` : `${formatarPreco(premisa.valor)}%`}
                                </Text>
                            </Flex>
                        ))}
                    </VStack>
                </Box>
            )}
        </VStack>
    );
};

const sanitizarPercent = (raw) => {
    let value = String(raw).replace(/[^0-9,]/g, '');
    const parts = value.split(',');
    if (parts.length > 2) {
        value = parts[0] + ',' + parts.slice(1).join('');
    }
    return value;
};

const percentParaNumero = (raw) => {
    if (raw === '' || raw === null || raw === undefined) return null;
    const n = parseFloat(String(raw).replace(',', '.'));
    return isNaN(n) ? null : n;
};

const FreteEnvioTab = () => {
    const toast = useToast();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [campos, setCampos] = useState([]);
    const [inicial, setInicial] = useState([]);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const data = await getConfiguracoes();
                const doFrete = (data || [])
                    .filter(c => CONFIGS_FRETE.includes(c.nome))
                    .map(c => ({ ...c, valor: String(c.valor).replace('.', ',') }));
                setCampos(doFrete);
                setInicial(JSON.parse(JSON.stringify(doFrete)));
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const handleChange = (nome, raw) => {
        let value = raw.replace(/[^0-9,]/g, '');
        const parts = value.split(',');
        if (parts.length > 2) {
            value = parts[0] + ',' + parts.slice(1).join('');
        }
        setCampos(prev => prev.map(c => (c.nome === nome ? { ...c, valor: value } : c)));
    };

    const handleSalvar = async () => {
        setSaving(true);
        try {
            await updateConfiguracoes(campos.map(c => ({
                ...c,
                valor: parseFloat(String(c.valor).replace(',', '.')) || 0,
            })));
            toast({ title: 'Limites de alerta salvos!', status: 'success', duration: 3000, isClosable: true });
            setInicial(JSON.parse(JSON.stringify(campos)));
        } catch (error) {
            toast({ title: 'Erro ao salvar os limites.', status: 'error', duration: 5000, isClosable: true });
        } finally {
            setSaving(false);
        }
    };

    const hasChanges = JSON.stringify(campos) !== JSON.stringify(inicial);

    if (loading) {
        return <Flex justify="center" py={8}><Spinner size="lg" color="orange.400" /></Flex>;
    }

    return (
        <Box p={{ base: 2, md: 6 }}>
            <Heading as="h4" size="sm" textAlign="left" mb={2}>Alerta de Divergência de Frete</Heading>
            <Text textAlign="left" color="gray.600" fontSize="sm" mb={5}>
                Na gestão de despacho, o modal do Melhor Envio compara cada cotação com o frete que o
                cliente pagou no pedido. Quando a opção fica acima dos dois limites abaixo ao mesmo tempo,
                ela é destacada e o operador é avisado para consultar a gestão antes de escolher. Exigir as
                duas condições evita alarme em frete barato, onde um percentual alto representa poucos reais.
                O alerta apenas sinaliza: não bloqueia a escolha.
            </Text>

            {campos.length === 0 ? (
                <Text fontSize="sm" color="gray.500">
                    Limites não encontrados nas configurações gerais.
                </Text>
            ) : (
                <VStack spacing={5} align="start" maxW="360px">
                    {campos.map(campo => {
                        const isPercent = campo.label.includes('%');
                        return (
                            <FormControl key={campo.nome}>
                                <FormLabel htmlFor={campo.nome} mb={1} fontSize="md" textAlign="left">
                                    {isPercent ? 'Diferença mínima em percentual' : 'Diferença mínima em reais'}
                                </FormLabel>
                                <InputGroup>
                                    {!isPercent && <InputLeftAddon>R$</InputLeftAddon>}
                                    <Input
                                        id={campo.nome}
                                        type="text"
                                        bg="white"
                                        value={campo.valor}
                                        onChange={(e) => handleChange(campo.nome, e.target.value)}
                                    />
                                    {isPercent && <InputRightAddon>%</InputRightAddon>}
                                </InputGroup>
                            </FormControl>
                        );
                    })}
                </VStack>
            )}

            <Flex justify="center" mt={8}>
                <Button
                    leftIcon={<FaSave />}
                    colorScheme="green"
                    onClick={handleSalvar}
                    isLoading={saving}
                    isDisabled={!hasChanges || saving}
                >
                    Salvar Limites
                </Button>
            </Flex>
        </Box>
    );
};

const MargemFornecedoresTab = () => {
    const toast = useToast();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [fornecedores, setFornecedores] = useState([]);
    const [inicial, setInicial] = useState([]);

    const carregar = useCallback(async () => {
        setLoading(true);
        try {
            const data = await getMargensFornecedores();
            if (data) {
                const formatted = data.map(f => ({
                    ...f,
                    margem_contribuicao_alvo: f.margem_contribuicao_alvo !== null
                        ? String(f.margem_contribuicao_alvo).replace('.', ',')
                        : '',
                }));
                setFornecedores(formatted);
                setInicial(JSON.parse(JSON.stringify(formatted)));
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { carregar(); }, [carregar]);

    const handleChange = (id, raw) => {
        const value = sanitizarPercent(raw);
        setFornecedores(prev => prev.map(f => f.id === id ? { ...f, margem_contribuicao_alvo: value } : f));
    };

    const handleSalvar = async () => {
        setSaving(true);
        try {
            const margens = fornecedores.map(f => ({
                id: f.id,
                margem_contribuicao_alvo: percentParaNumero(f.margem_contribuicao_alvo),
            }));
            await updateMargensFornecedores(margens);
            toast({ title: 'Margens salvas com sucesso!', status: 'success', duration: 3000, isClosable: true });
            setInicial(JSON.parse(JSON.stringify(fornecedores)));
        } catch (error) {
            toast({ title: 'Erro ao salvar margens.', status: 'error', duration: 5000, isClosable: true });
        } finally {
            setSaving(false);
        }
    };

    const hasChanges = JSON.stringify(fornecedores) !== JSON.stringify(inicial);

    if (loading) {
        return <Flex justify="center" py={8}><Spinner size="lg" color="orange.400" /></Flex>;
    }

    return (
        <Box p={{ base: 2, md: 6 }}>
            <Text textAlign="left" color="gray.600" fontSize="sm" mb={4}>
                Defina a margem de contribuição alvo para cada fornecedor. Deixe em branco para usar a margem global.
            </Text>
            <Box overflowX="auto">
                <Table variant="simple" size="md">
                    <Thead bg="gray.100">
                        <Tr>
                            <Th>Fornecedor</Th>
                            <Th>CNPJ</Th>
                            <Th maxW="220px">Margem Alvo (%)</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {fornecedores.map(f => (
                            <Tr key={f.id}>
                                <Td fontWeight="semibold">{f.nome}</Td>
                                <Td color="gray.500" fontSize="sm">{f.cnpj || '—'}</Td>
                                <Td>
                                    <InputGroup size="sm" maxW="180px">
                                        <Input
                                            type="text"
                                            bg="white"
                                            placeholder="ex: 35"
                                            value={f.margem_contribuicao_alvo}
                                            onChange={(e) => handleChange(f.id, e.target.value)}
                                        />
                                        <InputRightAddon>%</InputRightAddon>
                                    </InputGroup>
                                </Td>
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            </Box>
            <Flex justify="center" mt={6}>
                <Button
                    leftIcon={<FaSave />}
                    colorScheme="green"
                    onClick={handleSalvar}
                    isLoading={saving}
                    isDisabled={!hasChanges || saving}
                >
                    Salvar Margens
                </Button>
            </Flex>
        </Box>
    );
};

// Margem própria por conta do ML: preenchida vale para todos os anúncios da conta (por cima
// de marca e fornecedor); em branco a conta segue a regra normal.
const MargemContasMlTab = () => {
    const toast = useToast();
    const tamanhoTabela = useBreakpointValue({ base: 'sm', md: 'md' });
    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState(false);
    const [saving, setSaving] = useState(false);
    const [contas, setContas] = useState([]);
    const [inicial, setInicial] = useState([]);
    const [margemGlobal, setMargemGlobal] = useState(null);

    const carregar = useCallback(async () => {
        setLoading(true);
        setErro(false);
        try {
            const data = await getMargensContasMl();
            if (data && Array.isArray(data.contas)) {
                const formatted = data.contas.map(c => ({
                    ...c,
                    margem_contribuicao_alvo: c.margem_contribuicao_alvo !== null
                        ? String(c.margem_contribuicao_alvo).replace('.', ',')
                        : '',
                }));
                setContas(formatted);
                setInicial(JSON.parse(JSON.stringify(formatted)));
                setMargemGlobal(data.margem_global);
            } else {
                setErro(true);
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { carregar(); }, [carregar]);

    const handleChange = (conta, raw) => {
        const value = sanitizarPercent(raw);
        setContas(prev => prev.map(c => c.conta === conta ? { ...c, margem_contribuicao_alvo: value } : c));
    };

    const foraDaFaixa = contas.some(c => {
        if (c.margem_contribuicao_alvo === '') return false;
        const n = percentParaNumero(c.margem_contribuicao_alvo);
        return n === null || n < 0 || n > 99;
    });

    const handleSalvar = async () => {
        setSaving(true);
        try {
            const margens = contas.map(c => ({
                conta: c.conta,
                margem_contribuicao_alvo: percentParaNumero(c.margem_contribuicao_alvo),
            }));
            await updateMargensContasMl(margens);
            toast({
                title: 'Margens das contas salvas!',
                description: 'Os anúncios recebem o preço novo nas próximas atualizações de preço.',
                status: 'success',
                duration: 4000,
                isClosable: true,
            });
            setInicial(JSON.parse(JSON.stringify(contas)));
        } catch (error) {
            const semPermissao = error?.response?.status === 403;
            toast({
                title: 'Erro ao salvar margens.',
                description: semPermissao ? 'Você não tem permissão para alterar.' : 'Confira os valores (0 a 99%) e tente de novo.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setSaving(false);
        }
    };

    const hasChanges = JSON.stringify(contas) !== JSON.stringify(inicial);
    const globalTexto = margemGlobal !== null && margemGlobal !== undefined
        ? `${String(margemGlobal).replace('.', ',')}%`
        : 'global';

    if (loading) {
        return <Flex justify="center" py={8}><Spinner size="lg" color="orange.400" /></Flex>;
    }

    if (erro) {
        return (
            <Flex direction="column" align="center" py={8} gap={3}>
                <Text color="gray.600" fontSize="sm">Não foi possível carregar as margens das contas.</Text>
                <Button size="sm" onClick={carregar}>Tentar de novo</Button>
            </Flex>
        );
    }

    return (
        <Box p={{ base: 2, md: 6 }}>
            <Text textAlign="left" color="gray.600" fontSize="sm" mb={2}>
                Margem de contribuição própria de cada conta do Mercado Livre. Preenchida, vale para todos os
                anúncios da conta, por cima da margem por marca e por fornecedor. Em branco, a conta segue a
                regra normal (marca, fornecedor ou a margem global de {globalTexto}).
            </Text>
            <Text textAlign="left" color="gray.500" fontSize="xs" mb={4}>
                O preço novo entra nas rotinas diárias dos fornecedores e nos anúncios criados daqui em diante.
                Produto em promoção (queima de estoque ou lista de desconto) segue o preço da promoção.
            </Text>
            <Box overflowX="auto">
                <Table variant="simple" size={tamanhoTabela}>
                    <Thead bg="gray.100">
                        <Tr>
                            <Th>Conta</Th>
                            <Th maxW="220px">Margem Alvo (%)</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {contas.length === 0 && (
                            <Tr>
                                <Td colSpan={2} textAlign="center" color="gray.500" fontSize="sm">
                                    Nenhuma conta do Mercado Livre cadastrada.
                                </Td>
                            </Tr>
                        )}
                        {contas.map(c => (
                            <Tr key={c.conta}>
                                <Td>
                                    <Text fontWeight="semibold" fontSize={{ base: 'sm', md: 'md' }}>Conta {c.conta}</Text>
                                    <Text color="gray.500" fontSize="xs">{c.nome}</Text>
                                </Td>
                                <Td>
                                    <InputGroup size="sm" maxW="180px">
                                        <Input
                                            type="text"
                                            inputMode="decimal"
                                            bg="white"
                                            placeholder="regra normal"
                                            aria-label={`Margem alvo da conta ${c.conta}`}
                                            value={c.margem_contribuicao_alvo}
                                            onChange={(e) => handleChange(c.conta, e.target.value)}
                                        />
                                        <InputRightAddon>%</InputRightAddon>
                                    </InputGroup>
                                </Td>
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            </Box>
            {foraDaFaixa && (
                <Text color="red.500" fontSize="sm" mt={3} textAlign="center">Use um valor de 0 a 99%.</Text>
            )}
            <Flex justify="center" mt={6}>
                <Button
                    leftIcon={<FaSave />}
                    colorScheme="green"
                    onClick={handleSalvar}
                    isLoading={saving}
                    isDisabled={!hasChanges || saving || foraDaFaixa}
                >
                    Salvar Margens
                </Button>
            </Flex>
        </Box>
    );
};

const MargemMarcasTab = () => {
    const toast = useToast();
    const cancelRef = React.useRef();
    const [loading, setLoading] = useState(true);
    const [marcas, setMarcas] = useState([]);
    const [busca, setBusca] = useState('');
    const [sugestoes, setSugestoes] = useState([]);
    const [buscandoSug, setBuscandoSug] = useState(false);
    const [novoNome, setNovoNome] = useState('');
    const [novaMargem, setNovaMargem] = useState('');
    const [salvando, setSalvando] = useState(false);
    const [confirmarRemover, setConfirmarRemover] = useState(null);

    const carregar = useCallback(async () => {
        setLoading(true);
        try {
            const data = await getMargensMarcas();
            setMarcas(data || []);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { carregar(); }, [carregar]);

    useEffect(() => {
        const timer = setTimeout(async () => {
            if (busca.trim().length === 0) {
                setSugestoes([]);
                return;
            }
            setBuscandoSug(true);
            try {
                const data = await getSugestoesMarcas(busca.trim());
                setSugestoes(data || []);
            } finally {
                setBuscandoSug(false);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [busca]);

    const handleSelecionarSugestao = (nome) => {
        setNovoNome(nome);
        setBusca('');
        setSugestoes([]);
    };

    const handleSalvar = async () => {
        const nome = novoNome.trim();
        const valor = percentParaNumero(novaMargem);
        if (!nome) {
            toast({ title: 'Informe o nome da marca.', status: 'warning', duration: 3000, isClosable: true });
            return;
        }
        if (valor === null || valor < 0 || valor >= 100) {
            toast({ title: 'Margem inválida (0 a 99).', status: 'warning', duration: 3000, isClosable: true });
            return;
        }
        setSalvando(true);
        try {
            await saveMargemMarca(nome, valor);
            toast({ title: 'Margem salva!', status: 'success', duration: 2500, isClosable: true });
            setNovoNome('');
            setNovaMargem('');
            await carregar();
        } catch (error) {
            toast({ title: 'Erro ao salvar margem.', status: 'error', duration: 5000, isClosable: true });
        } finally {
            setSalvando(false);
        }
    };

    const handleRemover = async () => {
        if (!confirmarRemover) return;
        const id = confirmarRemover.id;
        setConfirmarRemover(null);
        const ok = await deleteMargemMarca(id);
        if (ok) {
            toast({ title: 'Margem removida.', status: 'success', duration: 2500, isClosable: true });
            await carregar();
        } else {
            toast({ title: 'Erro ao remover.', status: 'error', duration: 5000, isClosable: true });
        }
    };

    if (loading) {
        return <Flex justify="center" py={8}><Spinner size="lg" color="orange.400" /></Flex>;
    }

    return (
        <Box p={{ base: 2, md: 6 }}>
            <Text textAlign="left" color="gray.600" fontSize="sm" mb={4}>
                Configure margens específicas para marcas (campo <code>nome_fabricante</code>). A margem da marca tem prioridade sobre a do fornecedor.
            </Text>

            <Box bg="gray.50" p={4} borderRadius="md" borderWidth="1px" mb={6}>
                <Heading as="h4" size="sm" mb={3}>Adicionar / Atualizar Marca</Heading>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3}>
                    <FormControl position="relative">
                        <FormLabel fontSize="sm">Buscar marca existente</FormLabel>
                        <Input
                            bg="white"
                            placeholder="Digite para buscar"
                            value={busca}
                            onChange={(e) => setBusca(e.target.value)}
                        />
                        {buscandoSug && <Text fontSize="xs" color="gray.500" mt={1}>Buscando…</Text>}
                        {sugestoes.length > 0 && (
                            <List
                                bg="white"
                                borderWidth="1px"
                                borderRadius="md"
                                mt={1}
                                maxH="220px"
                                overflowY="auto"
                                fontSize="sm"
                                position={{ base: "static", md: "absolute" }}
                                top={{ md: "100%" }}
                                left={{ md: 0 }}
                                right={{ md: 0 }}
                                zIndex={{ md: 10 }}
                                boxShadow={{ md: "md" }}
                            >
                                {sugestoes.map(s => (
                                    <ListItem
                                        key={s.nome_fabricante}
                                        px={3}
                                        py={2}
                                        cursor="pointer"
                                        _hover={{ bg: 'orange.50' }}
                                        onClick={() => handleSelecionarSugestao(s.nome_fabricante)}
                                    >
                                        <Flex justify="space-between" align="center">
                                            <Text>{s.nome_fabricante}</Text>
                                            <Badge colorScheme="gray">{s.total_produtos}</Badge>
                                        </Flex>
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </FormControl>
                    <FormControl>
                        <FormLabel fontSize="sm">Nome da Marca</FormLabel>
                        <Input
                            bg="white"
                            placeholder="ex: BOSCH"
                            value={novoNome}
                            onChange={(e) => setNovoNome(e.target.value.toUpperCase())}
                        />
                    </FormControl>
                    <FormControl>
                        <FormLabel fontSize="sm">Margem Alvo (%)</FormLabel>
                        <InputGroup>
                            <Input
                                bg="white"
                                placeholder="ex: 35"
                                value={novaMargem}
                                onChange={(e) => setNovaMargem(sanitizarPercent(e.target.value))}
                            />
                            <InputRightAddon>%</InputRightAddon>
                        </InputGroup>
                    </FormControl>
                </SimpleGrid>
                <Flex justify="flex-end" mt={4}>
                    <Button
                        leftIcon={<FaPlus />}
                        colorScheme="orange"
                        onClick={handleSalvar}
                        isLoading={salvando}
                    >
                        Salvar Marca
                    </Button>
                </Flex>
            </Box>

            <Heading as="h4" size="sm" mb={3} textAlign="left">Marcas Configuradas ({marcas.length})</Heading>
            <Box overflowX="auto">
                <Table variant="simple" size="md">
                    <Thead bg="gray.100">
                        <Tr>
                            <Th>Marca (nome_fabricante)</Th>
                            <Th maxW="160px">Margem Alvo (%)</Th>
                            <Th maxW="80px">Ações</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {marcas.length === 0 && (
                            <Tr>
                                <Td colSpan={3} textAlign="center" color="gray.500">
                                    Nenhuma marca configurada.
                                </Td>
                            </Tr>
                        )}
                        {marcas.map(m => (
                            <Tr key={m.id}>
                                <Td fontWeight="semibold">{m.nome_fabricante}</Td>
                                <Td>{Number(m.margem_contribuicao_alvo).toFixed(2).replace('.', ',')}%</Td>
                                <Td>
                                    <IconButton
                                        aria-label="Remover"
                                        size="sm"
                                        colorScheme="red"
                                        variant="ghost"
                                        icon={<FaTrash />}
                                        onClick={() => setConfirmarRemover(m)}
                                    />
                                </Td>
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            </Box>

            <AlertDialog
                isOpen={!!confirmarRemover}
                leastDestructiveRef={cancelRef}
                onClose={() => setConfirmarRemover(null)}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Remover margem
                        </AlertDialogHeader>
                        <AlertDialogBody>
                            Tem certeza que deseja remover a margem da marca <b>{confirmarRemover?.nome_fabricante}</b>?
                            Os produtos dessa marca passarão a usar a margem do fornecedor ou a global.
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={() => setConfirmarRemover(null)}>
                                Cancelar
                            </Button>
                            <Button colorScheme="red" onClick={handleRemover} ml={3}>
                                Remover
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Box>
    );
};

const STATUS_EXPEDICAO = {
    pendente: { label: 'Pendente', color: 'gray' },
    processando: { label: 'Processando', color: 'blue' },
    concluida: { label: 'Concluída', color: 'green' },
    cancelada: { label: 'Cancelada', color: 'orange' },
    erro: { label: 'Erro', color: 'red' },
};

const ExpedicaoMLTab = () => {
    const toast = useToast();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [fornecedores, setFornecedores] = useState([]);
    const [inicial, setInicial] = useState([]);
    const [atualizacoes, setAtualizacoes] = useState([]);

    const carregarFornecedores = useCallback(async () => {
        const data = await getExpedicaoFornecedores();
        if (data) {
            const formatted = data.map(f => ({
                ...f,
                dias_expedicao_ml: f.dias_expedicao_ml !== null ? String(f.dias_expedicao_ml) : '',
            }));
            setFornecedores(formatted);
            setInicial(JSON.parse(JSON.stringify(formatted)));
        }
    }, []);

    const carregarAtualizacoes = useCallback(async () => {
        const data = await getExpedicaoAtualizacoes();
        if (data) setAtualizacoes(data);
    }, []);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                await Promise.all([carregarFornecedores(), carregarAtualizacoes()]);
            } finally {
                setLoading(false);
            }
        })();
    }, [carregarFornecedores, carregarAtualizacoes]);

    const temAtiva = atualizacoes.some(a => a.status === 'pendente' || a.status === 'processando');

    useEffect(() => {
        if (!temAtiva) return;
        const timer = setInterval(carregarAtualizacoes, 5000);
        return () => clearInterval(timer);
    }, [temAtiva, carregarAtualizacoes]);

    const handleChange = (id, raw) => {
        const value = raw.replace(/\D/g, '').slice(0, 2);
        setFornecedores(prev => prev.map(f => f.id === id ? { ...f, dias_expedicao_ml: value } : f));
    };

    const handleSalvar = async () => {
        setSaving(true);
        try {
            // Prazo automático (Compras de Fábrica) não é editado aqui.
            const payload = fornecedores.filter(f => !f.prazo_automatico).map(f => ({
                id: f.id,
                dias_expedicao_ml: f.dias_expedicao_ml === '' ? null : parseInt(f.dias_expedicao_ml, 10),
            }));
            const res = await updateExpedicaoFornecedores(payload);
            toast({
                title: res?.message || 'Dias de expedição salvos!',
                status: 'success',
                duration: 4000,
                isClosable: true,
            });
            setInicial(JSON.parse(JSON.stringify(fornecedores)));
            await carregarAtualizacoes();
        } catch (error) {
            toast({ title: 'Erro ao salvar dias de expedição.', status: 'error', duration: 5000, isClosable: true });
        } finally {
            setSaving(false);
        }
    };

    const handleCancelar = async (id) => {
        try {
            await cancelarExpedicaoAtualizacao(id);
            toast({ title: 'Atualização cancelada.', status: 'info', duration: 3000, isClosable: true });
        } catch (error) {
            toast({ title: 'Erro ao cancelar atualização.', status: 'error', duration: 5000, isClosable: true });
        }
        await carregarAtualizacoes();
    };

    const hasChanges = JSON.stringify(fornecedores) !== JSON.stringify(inicial);
    const fmt = (n) => Number(n || 0).toLocaleString('pt-BR');

    if (loading) {
        return <Flex justify="center" py={8}><Spinner size="lg" color="orange.400" /></Flex>;
    }

    return (
        <Box p={{ base: 2, md: 6 }}>
            <Text textAlign="left" color="gray.600" fontSize="sm" mb={4}>
                Prazo de expedição (dias úteis de manuseio) por fornecedor. Vale para todos os canais:
                anúncios do Mercado Livre, prazo que o site mostra ao cliente, data de despacho das vendas
                do site e das vendas manuais. Em branco = sem prazo. Novos anúncios já são criados com o
                prazo configurado; ao salvar uma alteração, um job ajusta todos os anúncios ativos do
                fornecedor — acompanhe o progresso abaixo. Vendas já importadas mantêm a data que receberam.
                Fornecedor de compra de fábrica tem o prazo recalculado sozinho (tela Compras de Fábrica).
            </Text>
            <Box overflowX="auto">
                <Table variant="simple" size="md">
                    <Thead bg="gray.100">
                        <Tr>
                            <Th>Fornecedor</Th>
                            <Th>CNPJ</Th>
                            <Th isNumeric>Anúncios Ativos</Th>
                            <Th maxW="200px">Dias de Expedição</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {fornecedores.map(f => (
                            <Tr key={f.id}>
                                <Td fontWeight="semibold">{f.nome}</Td>
                                <Td color="gray.500" fontSize="sm">{f.cnpj || '—'}</Td>
                                <Td isNumeric>{fmt(f.anuncios_ativos)}</Td>
                                <Td>
                                    {f.prazo_automatico ? (
                                        <HStack spacing={2}>
                                            <Badge colorScheme="blue">automático</Badge>
                                            <Text fontSize="sm" color="gray.600">
                                                {f.dias_expedicao_ml ? `${f.dias_expedicao_ml} dias hoje` : 'sem prazo'}
                                            </Text>
                                        </HStack>
                                    ) : (
                                        <InputGroup size="sm" maxW="160px">
                                            <Input
                                                type="text"
                                                bg="white"
                                                placeholder="sem prazo"
                                                value={f.dias_expedicao_ml}
                                                onChange={(e) => handleChange(f.id, e.target.value)}
                                            />
                                            <InputRightAddon>dias</InputRightAddon>
                                        </InputGroup>
                                    )}
                                </Td>
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            </Box>
            <Flex justify="center" mt={6}>
                <Button
                    leftIcon={<FaSave />}
                    colorScheme="green"
                    onClick={handleSalvar}
                    isLoading={saving}
                    isDisabled={!hasChanges || saving}
                >
                    Salvar e Atualizar Anúncios
                </Button>
            </Flex>

            <Divider my={6} />

            <Flex align="center" justify="space-between" mb={3}>
                <Heading as="h4" size="sm" textAlign="left">Progresso das Atualizações</Heading>
                {temAtiva && <Badge colorScheme="blue">atualizando…</Badge>}
            </Flex>
            {atualizacoes.length === 0 ? (
                <Text color="gray.500" fontSize="sm" textAlign="left">Nenhuma atualização disparada ainda.</Text>
            ) : (
                <VStack spacing={3} align="stretch">
                    {atualizacoes.map(a => {
                        const st = STATUS_EXPEDICAO[a.status] || { label: a.status, color: 'gray' };
                        const pct = a.total_anuncios > 0
                            ? Math.min(100, Math.round((a.processados / a.total_anuncios) * 100))
                            : (a.status === 'concluida' ? 100 : 0);
                        const ativa = a.status === 'pendente' || a.status === 'processando';
                        return (
                            <Box key={a.id} p={3} borderWidth="1px" borderRadius="md" bg="gray.50">
                                <Flex justify="space-between" align="center" mb={1} gap={2} flexWrap="wrap">
                                    <HStack spacing={2}>
                                        <Text fontWeight="semibold" fontSize="sm">
                                            {a.fornecedor_nome || `Fornecedor ${a.fornecedor_id}`}
                                        </Text>
                                        <Badge colorScheme="purple">{a.dias ? `${a.dias} dias` : 'sem prazo'}</Badge>
                                        <Badge colorScheme={st.color}>{st.label}</Badge>
                                    </HStack>
                                    <HStack spacing={3}>
                                        <Text fontSize="xs" color="gray.600">
                                            {fmt(a.processados)}/{fmt(a.total_anuncios)} ({pct}%)
                                            {a.falhas > 0 ? ` · ${fmt(a.falhas)} falhas` : ''}
                                        </Text>
                                        {ativa && (
                                            <IconButton
                                                aria-label="Cancelar atualização"
                                                size="xs"
                                                colorScheme="red"
                                                variant="ghost"
                                                icon={<FaBan />}
                                                onClick={() => handleCancelar(a.id)}
                                            />
                                        )}
                                    </HStack>
                                </Flex>
                                <Progress value={pct} size="sm" colorScheme={st.color} borderRadius="md" hasStripe={ativa} isAnimated={ativa} />
                                <Text fontSize="xs" color="gray.500" textAlign="left" mt={1}>
                                    {a.created_at ? new Date(a.created_at).toLocaleString('pt-BR') : ''}
                                    {a.erro ? ` · Erro: ${String(a.erro).substring(0, 150)}` : ''}
                                </Text>
                            </Box>
                        );
                    })}
                </VStack>
            )}
        </Box>
    );
};

export default Configuracoes;
